import {
	KeyboardArrowDownOutlined,
	KeyboardArrowRightOutlined
} from "@mui/icons-material";
import { Collapse, FormControl, FormLabel, TextField, Typography } from "@mui/material";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid2";
import MenuItem from "@mui/material/MenuItem";
import { DatePicker } from "@mui/x-date-pickers-pro";
import axios from "axios";
import _ from "lodash";
import { enqueueSnackbar } from "notistack";
import React, { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import FormBoxTitle from "../../../components/mui/app/formBoxTitle/formBoxTitle";
import CustomAutocomplete from "../../../components/mui/form/autocomplete/autocomplete";
import { API } from "../../../helpers/constants";
import { handleAxiosErrors, logoutUnauthorizedUser } from "../../../helpers/errorHandling";
import { check_lists_filter_site } from "../../../helpers/rights/rights";
import { formControlDefaults, textFieldSelectDefaults } from "../../../helpers/theme/defaultProps";
import useUserAction from "../../../hooks/user/useUserAction";
import useUserData from "../../../hooks/user/useUserData";
import { CHECKLISTS } from "../../../locales/pages/namespaces";
import PermissionHOC from "../../../modules/permission/components/permissionHOC/permissionHOC";
import { useHasPermission } from "../../../modules/permission/hooks/useHasPermission";
import { initialFilters, mapFilters } from "../checkListsHistory";

const CheckListFilters = (props) => {
	const {
		filters,
		setFilters,
		dataManager,
		loading,
		sites
	} = props
	const { t } = useTranslation(CHECKLISTS)
	const { token, isLoggedIn } = useUserData()
	const { userLogout } = useUserAction()
	const [loadingSearch, setLoadingSearch] = useState(false)
	const [sitesData, setSitesData] = useState([...sites])
	const [open, setOpen] = useState(true)
	
	const searchSite = useCallback((evt, name) => {
		setLoadingSearch(true)
		axios({
			method: 'post',
			headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
			data: {
				query: evt.target.value,
				Type: 'Site',
				CustId: 1314, // Μόνο η alpha bank έχει καλώς έχειν
			},
			url: `${API}/en/customer/address/search`
		}).then((response) => {
			setSitesData(_.orderBy(response.data.data, ['Description'], ['asc']))
			setLoadingSearch(false)
		}).catch((error) => {
			setLoadingSearch(false)
			enqueueSnackbar(handleAxiosErrors(error, t('An error occurred!')), {
				variant: 'error',
				autoHideDuration: 3000,
			})
			logoutUnauthorizedUser(error, isLoggedIn, userLogout)
		})
	}, [t, isLoggedIn, userLogout])
	
	return (
		<Box sx={{ display: 'flex', flexDirection: 'column', marginBottom: 1 }}>
			<FormBoxTitle
				title={t('Filter Options')}
			>
				<Typography
					variant={'button'}
					component={'div'}
					onClick={() => setOpen(!open)}
					sx={{
						cursor: 'pointer',
						padding: 0.5,
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center'
					}}
				>
					{open ? <KeyboardArrowDownOutlined sx={{ fontSize: 16 }}/> :
						<KeyboardArrowRightOutlined sx={{ fontSize: 16 }}/>}
				</Typography>
			</FormBoxTitle>
			<Collapse in={open}>
				<Grid container spacing={1} alignItems={'flex-end'}>
					<PermissionHOC requiredPermissions={[check_lists_filter_site]}>
						<Grid size={{ xs: 12, sm: 6, md: 6, lg: 5 }}>
							<FormControl
								{...formControlDefaults}
							>
								<FormLabel>{t('Site')}</FormLabel>
								<CustomAutocomplete
									identifier="id"
									name="site"
									onChange={(evt, newValue, reason) => {
										setFilters({
											...filters,
											site: newValue
										})
										if (reason === 'clear') {
											setSitesData([...sites])
										}
									}}
									value={filters.site}
									options={sitesData}
									labelKey={'Description'}  //LoginName or AssigneeName
									placeholder={t('Select one or more..')}
									otherprops={{
										selectOnFocus: true,
										handleHomeEndKeys: true,
										getOptionKey: (option) => (option?.id)
									}}
									loading={loadingSearch}
									onInputChange={(evt) => searchSite(evt)}
								/>
							</FormControl>
						</Grid>
					</PermissionHOC>
					<Grid size={{ xs: 12, sm: 6, md: 6, lg: 2 }}>
						<FormControl
							{...formControlDefaults}
						>
							<FormLabel>{t('Month')}</FormLabel>
							<DatePicker
								slotProps={{
									textField: {
										size: 'small',
										variant: 'standard'
									}
								}}
								name="month"
								views={['month', 'year']}
								value={filters.month}
								onChange={(newValue) => {
									setFilters({
										...filters,
										month: newValue ? newValue.startOf('month') : null
									})
								}}
							/>
						</FormControl>
					</Grid>
					<Grid size={{ xs: 12, sm: 6, md: 6, lg: 2 }}>
						<FormControl
							{...formControlDefaults}
						>
							<FormLabel>{t('Submitted')}</FormLabel>
							<TextField
								{...textFieldSelectDefaults}
								onChange={(evt) => {
									setFilters({
										...filters,
										isSubmitted: evt.target.value
									})
								}}
								value={filters.isSubmitted}
								name={'isSubmitted'}
							>
								<MenuItem value={null} sx={{ padding: 1 }}/>
								<MenuItem value={1} sx={{ padding: 1 }}>
									{t('Yes')}
								</MenuItem>
								<MenuItem value={0} sx={{ padding: 1 }}>
									{t('No')}
								</MenuItem>
							</TextField>
						</FormControl>
					</Grid>
					<Grid size={{ xs: 12 }} sx={{ background: '#f6f6f6', p: 0.4 }}>
						<Box display={'flex'} alignItems={'flex-end'} justifyContent={'center'}
							 flexWrap={'wrap'} gap={1}>
							<Button
								loading={loading}
								onClick={() => {
									const params = {
										...mapFilters(filters)
									}
									dataManager(params)
								}}
								variant="outlined"
								color={'success'}
							>
								{t('Search')}
							</Button>
							<Button
								loading={loading}
								onClick={() => {
									dataManager({
										ucAddrId: null,
										ucReferenceDt: null,
										isSubmitted: null,
									})
									setFilters(initialFilters)
									setSitesData([...sites])
								}}
								variant="outlined"
								color={'error'}
							>
								{t('Clear')}
							</Button>
						</Box>
					</Grid>
					<Grid size={12}>
						<Divider/>
					</Grid>
				</Grid>
			</Collapse>
		</Box>
	);
};

export default CheckListFilters